import type { RefObject } from 'react';
import { useEffect } from 'react';

type UseNestedPinnedTransformArgs = {
  /** ネストテーブルの<table>要素への参照 */
  tableRef: RefObject<HTMLTableElement | null>;
  /** 親テーブルのスクロールプロキシRef */
  proxyRef: RefObject<HTMLDivElement | null>;
  /** 親テーブルのコンテナRef（コンテナ幅の取得に使用） */
  containerRef: RefObject<HTMLDivElement | null>;
};

/**
 * ネストテーブルのピン留めセルをtransformで位置補正するフック
 *
 * プロキシスクロール方式では、親テーブルが `translate3d(-scrollLeft, 0, 0)` で横移動し、
 * ネストテーブルもその中に含まれるため同様に移動する。
 * 親テーブルの `useProxyScroll` は `:scope >` セレクタでネストテーブルのセルを除外しているため、
 * ネストテーブルのピン留めセルは自身で位置補正する必要がある。
 *
 * 親テーブルに transform が適用されているため、ネストテーブル内セルの offsetParent が
 * 親テーブルになる。cell.offsetLeft は親テーブル基準の値を返すため、
 * 親テーブルの useProxyScroll と同一の計算式（containerWidth基準 + offsetLeft）で
 * 正確なピン留め位置を算出できる。
 */
export const useNestedPinnedTransform = ({
  tableRef,
  proxyRef,
  containerRef,
}: UseNestedPinnedTransformArgs) => {
  useEffect(() => {
    const proxy = proxyRef.current;
    const table = tableRef.current;
    const container = containerRef.current;
    if (!proxy || !table || !container) return;

    let rafId: number | null = null;

    // セルの offsetLeft を親ボディテーブル基準に補正する
    // <thead position:sticky> 内のセルは offsetParent が <thead> になるため、
    // <thead> 自身の offsetLeft を加算して親テーブル基準に揃える
    const getCellLeftFromBodyTable = (cell: HTMLElement): number => {
      const section = cell.closest('thead, tbody') as HTMLElement | null;
      const isStickySection = section && getComputedStyle(section).position === 'sticky';
      return isStickySection ? cell.offsetLeft + section.offsetLeft : cell.offsetLeft;
    };

    const updatePinnedCells = () => {
      const sl = proxy.scrollLeft;
      const containerWidth = container.clientWidth;

      // 親テーブルの useProxyScroll と同一の計算式を使用
      // getCellLeftFromBodyTable で offsetLeft を親ボディテーブル基準に統一

      // 左ピン留めセル
      const pinnedLeftCells = table.querySelectorAll<HTMLElement>(
        ':scope > thead > tr > [data-pinned-left], :scope > tbody > tr > [data-pinned-left]',
      );
      pinnedLeftCells.forEach((cell) => {
        const offset = Number(cell.getAttribute('data-pinned-left'));
        cell.style.transform = `translateX(${sl + offset}px)`;
      });

      // 右ピン留めセル
      const pinnedRightCells = table.querySelectorAll<HTMLElement>(
        ':scope > thead > tr > [data-pinned-right], :scope > tbody > tr > [data-pinned-right]',
      );
      pinnedRightCells.forEach((cell) => {
        const offset = Number(cell.getAttribute('data-pinned-right'));
        const cellLeft = getCellLeftFromBodyTable(cell);
        const cellWidth = cell.offsetWidth;
        const targetLeft = containerWidth - cellWidth - offset;
        cell.style.transform = `translateX(${targetLeft - cellLeft + sl}px)`;
      });

      rafId = null;
    };

    const handleScroll = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(updatePinnedCells);
    };

    // 初回実行（スクロール位置が0でない場合に対応）
    updatePinnedCells();

    proxy.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      proxy.removeEventListener('scroll', handleScroll);
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [tableRef, proxyRef, containerRef]);
};
